import { IonProgressBar } from "@ionic/react";
import { ExtractRouteParams, Redirect, RouteComponentProps } from "react-router";
import { AUTH_URLS, createDocumentsUrl } from "src/pages/authenticated/Routes";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import { Alert, AlertColor, Icon } from "swing-components";

import { ContentSingleColumn, MarkdownContent } from "~components";
import { translate as t, type DocWorkflowData, type KeyWithEmptyFnParam } from "~utils";

import { Step } from "../../components/step";
import { useTask, type TaskOnChange } from "../../hooks/use-task";
import { calcProgress } from "../../utils";

type ProgressIndicatorProps = {
  value: number; // 0-100
  isComplete: boolean;
};

function ProgressIndicator(props: ProgressIndicatorProps) {
  const { value, isComplete } = props;
  const color = isComplete ? "var(--swing-green500)" : "var(--swing-blue500)";

  return (
    <IonProgressBar
      value={value / 100}
      css={{
        height: "var(--spacing-base)",
        "--progress-background": color,
        "--background": "var(--swing-slate200)",
      }}
    />
  );
}

type TaskHeaderProps = {
  title: string;
  progress: number;
  isComplete: boolean;
  onClose: () => void;
};

function TaskHeader(props: TaskHeaderProps) {
  const { title, progress, isComplete, onClose } = props;

  return (
    <header
      css={{
        width: "100%",
        boxShadow: "var(--swing-box-shadow)",
        zIndex: 1,
      }}
    >
      <div
        css={{
          padding: "calc(var(--ion-safe-area-top, 0px) + var(--spacing-2xl)) var(--spacing-2xl)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-sm)",
        }}
      >
        <div css={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 css={{ margin: 0 }}>{title}</h3>
          <button
            type="button"
            css={{
              background: "var(--swing-white200)",
              color: "var(--swing-blue500)",
              padding: "var(--spacing-sm) 0px var(--spacing-sm) var(--spacing-lg)",
              border: "none",
              cursor: "pointer",
              fontSize: "var(--swing-font-sm)",
              fontWeight: "var(--swing-font-medium)",
            }}
            onClick={onClose}
          >
            <Icon name="Close" color="var(--swing-blue500)" />
          </button>
        </div>
        <ProgressIndicator value={progress} isComplete={isComplete} />
      </div>
    </header>
  );
}

type StepStatus = "UNDER_REVIEW" | "COMPLETED" | "ACTION_REQUIRED";

const stepAlertColors: Record<StepStatus, AlertColor> = {
  UNDER_REVIEW: "info",
  COMPLETED: "success",
  ACTION_REQUIRED: "warning",
};

const stepAlertTitles: Record<StepStatus, string> = {
  UNDER_REVIEW: "LABEL_UNDER_REVIEW",
  COMPLETED: "LABEL_COMPLETED",
  ACTION_REQUIRED: "LABEL_ACTION_REQUIRED",
};

type StepAlertProps = {
  storedStatus: StepStatus;
  storedMessage?: string;
  customMessage?: string;
};

function StepAlert(props: StepAlertProps) {
  const { storedStatus, storedMessage, customMessage } = props;
  const message = storedMessage
    ? storedMessage
    : customMessage
      ? t(customMessage as KeyWithEmptyFnParam)
      : "";

  if (!message) return null;

  return (
    <Alert
      showIcon
      color={stepAlertColors[storedStatus]}
      title={t(stepAlertTitles[storedStatus] as KeyWithEmptyFnParam)}
    >
      <MarkdownContent body={message} />
    </Alert>
  );
}

type TaskViewProps = {
  data: DocWorkflowData;
  onClose: () => void;
  onChange: TaskOnChange;
};

function TaskView(props: TaskViewProps) {
  const { data, onClose, onChange } = props;
  const { id, step, stepQuestionsData, stepAlert } = data;
  const title = t(id as unknown as KeyWithEmptyFnParam);
  const progress = calcProgress(data);
  const isComplete = data.step.isTaskComplete;

  return (
    <ScorePage title={title} hasHeader={false}>
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            alignItems: "center",
            color: "var(--swing-slate600)",
            backgroundColor: "var(--swing-white200)",
            marginBottom: "var(--spacing-6xl)",
          }}
        >
          <TaskHeader title={title} progress={progress} isComplete={isComplete} onClose={onClose} />
          <div
            css={{
              padding: "var(--spacing-6xl) var(--spacing-2xl)",
              width: "100%",
              backgroundColor: "var(--swing-white200)",
            }}
          >
            <div
              css={{
                maxWidth: 660,
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-6xl)",
              }}
            >
              {stepAlert ? <StepAlert {...stepAlert} /> : null}
              <Step task={id} data={{ ...step, state: stepQuestionsData }} onChange={onChange} />
            </div>
          </div>
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

type DocumentsTaskProps = RouteComponentProps<
  ExtractRouteParams<(typeof AUTH_URLS)["documentsTask"], string>
>;

export function DocumentsTask(props: DocumentsTaskProps) {
  const { match, history } = props;
  const { task, step } = match.params;

  const { taskData, onChange, isPending, isError } = useTask({ task, step });

  if (!task) {
    return <Redirect to={createDocumentsUrl()} />;
  }

  if (isPending) {
    return (
      <ScorePage title={t("SETTINGS_DOCUMENTS")} hasBack isLoading>
        <ContentSingleColumn>{null}</ContentSingleColumn>
      </ScorePage>
    );
  }

  if (isError || !taskData) {
    return (
      <ScorePage title={t("SETTINGS_DOCUMENTS")} hasBack>
        <ContentSingleColumn>
          <div css={{ padding: "var(--spacing-lg)", textAlign: "center" }}>
            {t("ERROR_GENERAL")}
          </div>
        </ContentSingleColumn>
      </ScorePage>
    );
  }

  return (
    <TaskView
      data={taskData}
      onChange={onChange}
      onClose={() => {
        history.push(createDocumentsUrl());
      }}
    />
  );
}
